import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";

import { cn } from "@src/lib/utils/cn";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b border-border-primary", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

// The chevron turns on the same curve as the content height; both drop their
// motion under prefers-reduced-motion.
const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "group/accordion-trigger flex flex-1 items-center justify-between gap-3 rounded-md py-4 text-left text-text-primary transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border disabled:cursor-not-allowed disabled:text-text-disabled",
        className,
      )}
      {...props}
    >
      <span className="min-w-0 flex-1 font-sans-v4 text-s-v4 font-medium-v4">
        {children}
      </span>
      <ChevronDownIcon className="size-3 shrink-0 text-icon-tertiary transition-[rotate,color] duration-[var(--accordion-close-dur)] ease-[var(--accordion-ease)] group-hover/accordion-trigger:text-icon-primary group-disabled/accordion-trigger:text-icon-disabled group-data-[state=open]/accordion-trigger:rotate-180 group-data-[state=open]/accordion-trigger:duration-[var(--accordion-open-dur)] motion-reduce:transition-none" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    contentClassName?: string;
  }
>(({ className, contentClassName, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      "overflow-hidden",
      contentClassName,
      "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=open]:animate-none",
    )}
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
